'use client';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';

import webStorageKeys from '@/constants/web-storage-keys';
import type { AppbarItemProps } from '../appbar-item.types';
import type { AppbarIconName } from '../../appbar-settings';
import { goldenDuelStorageService } from '@/services/golden-duel/golden-duel-storage.service';
import { useAnalyseAttribute } from '@/features/next-analyse';

import { Icon, SportIcon } from '@/components/icons/svg-icons';
import { Tooltip } from '@/components/widgets/tooltip';
import { tooltip } from 'styled-system/recipes';
import { AccountTooltip } from '@/features/profile/account-tooltip';
import { PackPopover } from '@/features/promotion/cwc-risk-free-bet/components/pack-popover';
import { PackVoucherBadge } from '@/features/badge/pack-voucher-badge';

import { appbarStyles } from '../../appbar.style';

export const AppbarItem = ({
	config,
	isSelected,
	isMars,
	isValidPromotionVoucherUser,
	statusCount,
	enableMarsRevisionNotice,
	needShowTooltip,
	onTooltipShown,
	showGoldenDuelTooltip,
	onClick,
}: AppbarItemProps) => {
	const t = useTranslations('General');
	const taggedElement = useAnalyseAttribute(config.analyseKey ?? '');
  
	return (
		<div
			className={appbarStyles.item}
			data-selected={isSelected}
			onClick={onClick}
			ref={taggedElement}
		>
			<AppbarIcon iconName={config.iconName} />
			<div className={appbarStyles.itemText}>{t(config.resourceKey)}</div>

			{config.key === 'Statement' && statusCount ? (
				<div className={appbarStyles.badge}>{statusCount}</div>
			) : null}

			{isMars && enableMarsRevisionNotice && config.tooltipResourceKey ? (
				<Tooltip
					message={needShowTooltip ? t(config.tooltipResourceKey) : ''}
					countdownTime={5000}
					showMessageDoAction={() =>
						localStorage.setItem(webStorageKeys.HAS_SHOWN_MARS_UPDATE_TOOLTIP_IN_APPBAR, 'true')
					}
					clearMessage={onTooltipShown}
					className={tooltip({ visual: 'basic', vertical: 'top', horizontal: 'right', size: 'sm' })}
				/>
			) : null}

			{isMars && ['Settings', 'Me'].includes(config.key) ? (
				<AccountTooltip position="appbar" />
			) : null}

			{config.key === 'Settings' && isValidPromotionVoucherUser ? (
				<PackPopover
					className={clsx(
						tooltip({ visual: 'basic', vertical: 'top', horizontal: 'right', size: 'sm' }),
						'promo-beginners-pack-tooltip'
					)}
					message={t('lbl_IDRRFHint')}
				/>
			) : null}

			{config.key === 'Promotion' && showGoldenDuelTooltip ? (
				<Tooltip
					message={t('lbl_LotteryLocation')}
					countdownTime={5000}
					showMessageDoAction={() => goldenDuelStorageService.markTooltipAsShown()}
					clearMessage={() => {}}
					className={tooltip({ visual: 'basic', vertical: 'top', horizontal: 'left', size: 'sm' })}
				/>
			) : null}

			{config.key === 'Me' && isValidPromotionVoucherUser ? (
				<PackVoucherBadge className={appbarStyles.badgePoint} />
			) : null}
		</div>
	);
};

const AppbarIcon = ({ iconName }: { iconName: AppbarIconName }) => {
	if (iconName === 'sport') {
		return <SportIcon sportId={1} />;
	};
	
	return <Icon name={iconName} />;
};